import { Head, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import { Users, UserCheck, Clock, FileSignature, ArrowRight } from 'lucide-react'

interface SubAgent {
  row_key: string
  id: number
  kind: 'agent' | 'invite'
  name: string
  email: string
  phone: string | null
  agent_type: string | null
  onboarding_status: string | null
  referral_status: string | null
  active_licenses: number | null
  // Omitted entirely when the organization hides commissions and equity.
  total_commissions?: number | null
  status_sort: string | null
  activated_at: string | null
  contract_signed_at: string | null
  document_sent_at: string | null
}

interface Props extends PageProps {
  sub_agents: SubAgent[]
  agent_name: string
  can_view_referrals: boolean
  show_commissions: boolean
}

export default function MyTeamIndex({ sub_agents, agent_name, can_view_referrals, show_commissions }: Props) {
  const formatCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    invited: 'bg-blue-100 text-blue-700',
    prospect: 'bg-gray-100 text-gray-700',
    contract_pending: 'bg-yellow-100 text-yellow-700',
    documents_pending: 'bg-yellow-100 text-yellow-700',
    training_pending: 'bg-orange-100 text-orange-700',
    inactive: 'bg-red-100 text-red-700'
  }

  const statusLabels: Record<string, string> = {
    active: 'Active',
    invited: 'Invited',
    prospect: 'Prospect',
    contract_pending: 'Contract Pending',
    documents_pending: 'Documents Pending',
    training_pending: 'Training Pending',
    inactive: 'Inactive'
  }

  const referralStatusColors: Record<string, string> = {
    invited: 'bg-blue-100 text-blue-700',
    document_signed: 'bg-green-100 text-green-700',
    past_agent: 'bg-gray-100 text-gray-700'
  }

  const referralStatusLabels: Record<string, string> = {
    invited: 'Invited',
    document_signed: 'Document Signed',
    past_agent: 'Past Agent'
  }

  const activeCount = sub_agents.filter(a => a.kind === 'agent' && a.onboarding_status === 'active').length
  const pendingCount = sub_agents.filter(a => a.kind === 'invite' || a.onboarding_status !== 'active').length
  const signedCount = sub_agents.filter(a => a.contract_signed_at).length

  const columns: Column<SubAgent>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (agent) => (
        <div id={`agent-${agent.id}`}>
          <div className="flex items-center gap-2">
            <span className="font-medium text-chestnut-charcoal">{agent.name}</span>
            {agent.kind === 'invite' && (
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-chestnut-gold/15 text-chestnut-gold whitespace-nowrap">
                Pending Invite
              </span>
            )}
          </div>
          <div className="text-sm text-chestnut-charcoal/60">{agent.email}</div>
        </div>
      )
    },
    {
      key: 'phone',
      header: 'Phone',
      sortable: true,
      render: (agent) => <span className="text-chestnut-charcoal/70">{agent.phone || '—'}</span>
    },
    {
      key: 'status_sort',
      header: 'Status',
      sortable: true,
      render: (agent) => agent.kind === 'invite' ? (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${referralStatusColors[agent.referral_status || ''] || 'bg-gray-100 text-gray-700'}`}>
          {referralStatusLabels[agent.referral_status || ''] || agent.referral_status}
        </span>
      ) : (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[agent.onboarding_status || ''] || 'bg-gray-100 text-gray-700'}`}>
          {statusLabels[agent.onboarding_status || ''] || agent.onboarding_status}
        </span>
      )
    },
    {
      key: 'contract_signed_at',
      header: 'Agreement',
      sortable: true,
      render: (agent) => agent.contract_signed_at ? (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 whitespace-nowrap">
          <FileSignature className="h-3.5 w-3.5" />
          Signed {new Date(agent.contract_signed_at).toLocaleDateString()}
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">
          Not signed yet
        </span>
      )
    },
    {
      key: 'active_licenses',
      header: 'Licenses',
      sortable: true,
      className: 'text-center',
      headerClassName: 'text-center',
      render: (agent) => agent.active_licenses == null
        ? <span className="text-chestnut-charcoal/40">—</span>
        : <span className="text-chestnut-charcoal">{agent.active_licenses}</span>
    },
    // Dropped entirely — not blanked out — when the organization hides
    // commissions, so no empty column is left behind.
    ...(show_commissions ? [{
      key: 'total_commissions',
      header: 'Total Commissions',
      sortable: true,
      className: 'text-right',
      headerClassName: 'text-right',
      render: (agent: SubAgent) => agent.total_commissions == null
        ? <span className="text-chestnut-charcoal/40">—</span>
        : <span className="font-medium text-chestnut-forest">{formatCurrency(agent.total_commissions)}</span>
    }] : []),
    {
      key: 'activated_at',
      header: 'Activated',
      sortable: true,
      render: (agent) => agent.activated_at
        ? new Date(agent.activated_at).toLocaleDateString()
        : <span className="text-chestnut-charcoal/40">—</span>
    }
  ]

  return (
    <AppLayout>
      <Head title="My Team" />

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">My Team</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Agents sponsored under {agent_name}</p>
          </div>
          {can_view_referrals && (
            <Link
              href="/referrals"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-chestnut-forest hover:text-chestnut-forest/80"
            >
              View My Referrals
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-chestnut-forest/10 rounded-lg">
                <Users className="h-5 w-5 text-chestnut-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{sub_agents.length}</p>
                <p className="text-xs text-chestnut-charcoal/60">Total Team Members</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <UserCheck className="h-5 w-5 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{activeCount}</p>
                <p className="text-xs text-chestnut-charcoal/60">Active Agents</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{pendingCount}</p>
                <p className="text-xs text-chestnut-charcoal/60">Pending Onboarding</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-chestnut-gray p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-chestnut-gold/15 rounded-lg">
                <FileSignature className="h-5 w-5 text-chestnut-gold" />
              </div>
              <div>
                <p className="text-2xl font-bold text-chestnut-charcoal">{signedCount}</p>
                <p className="text-xs text-chestnut-charcoal/60">Contracts Signed</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <DataTable
            data={sub_agents}
            columns={columns}
            searchPlaceholder="Search team members..."
            searchKeys={['name', 'email']}
            pageSize={15}
            emptyMessage="You don't have any team members yet."
            getRowKey={(agent) => agent.row_key}
          />
        </div>
      </div>
    </AppLayout>
  )
}
